import config from '@constants/config';
import type { HTTP_TIMEOUTS, UserData } from '@shared/types/http';
import {
  CORRELATION_HEADER,
  createCorrelationId,
  isCorrelationHeaderEnabled,
  readCorrelationId,
  rememberCorrelationId,
} from '@shared/utils/correlationId';
import { getEnv } from '@shared/utils/env';
import { getUserEncrypted } from '@shared/utils/storageUtils';
import { forcedLogout, isLogoutInProgress } from '@shared/utils/authCleanup';
import { renewToken, wasSessionRenewedRecently } from '@shared/utils/sessionUtils';
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const { ENABLE_COOKIES } = config;

function isPlainObjectWithStringMessage(value: unknown): value is { message: string } {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  if (!('message' in value)) {
    return false;
  }
  return typeof (value as { message: unknown }).message === 'string';
}

function toInterceptorError(reason: unknown): Error {
  if (reason instanceof Error) {
    return reason;
  }
  if (typeof reason === 'string') {
    return new Error(reason);
  }

  const message = isPlainObjectWithStringMessage(reason) ? reason.message : 'Unknown error';
  const err = new Error(message);
  if (reason !== null && typeof reason === 'object') {
    Object.assign(err, reason);
  }
  return err;
}

export const TIMEOUTS: typeof HTTP_TIMEOUTS = {
  DEFAULT: 0,
  RENEW_SESSION: 15000,
} as const;

const getUserSafely = (): UserData | null => {
  try {
    return getUserEncrypted();
  } catch (error) {
    console.error('[apiClient] Failed to retrieve user data:', error);
    return null;
  }
};

const apiClient: AxiosInstance = axios.create({
  baseURL: getEnv().VITE_API_URL,
  withCredentials: true,
  timeout: TIMEOUTS.DEFAULT,
  headers: {
    'x-correspondent': getUserSafely()?.correspondent?.toString() ?? '',
    'Content-type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('TOKEN');
    const user = getUserSafely();
    const url = config.url ?? '';

    if (user?.correspondent) {
      config.headers['x-correspondent'] = user.correspondent.toString();
    }

    if (token && !ENABLE_COOKIES) {
      config.headers.token = token;
    }

    if (isCorrelationHeaderEnabled()) {
      const correlationId = readCorrelationId(config.headers) ?? createCorrelationId();
      config.headers[CORRELATION_HEADER] = correlationId;
      rememberCorrelationId(config.method, url, correlationId);
    }

    if (url.includes('renew-session')) {
      config.timeout = TIMEOUTS.RENEW_SESSION;
    } else {
      config.timeout = TIMEOUTS.DEFAULT;
    }

    return config;
  },
  (error: unknown) => {
    console.error('[apiClient] Request interceptor error:', error);
    const rejection: Error = toInterceptorError(error);
    return Promise.reject(rejection);
  }
);

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const isLogoutUrl = (url: string): boolean => url.includes('/logout/');

const isSilent401Url = (url: string): boolean =>
  isLogoutUrl(url) || url.includes('/remittances/payment/cancel');

const isRenewableUrl = (url: string): boolean =>
  !url.includes('renew-session') &&
  !url.includes('/login') &&
  !url.includes('/validate-user') &&
  !isLogoutUrl(url);

const getAuthErrorCode = (error: unknown): string | undefined => {
  const data = (error as { response?: { data?: { errors?: Array<{ code?: string }> } } }).response?.data;
  return data?.errors?.[0]?.code;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    const originalRequest = (error as { config?: RetryableConfig }).config;
    const status = (error as { response?: { status?: number } }).response?.status;
    const url = originalRequest?.url ?? '';

    if (status !== 401) {
      return Promise.reject(toInterceptorError(error));
    }

    if (isLogoutInProgress()) {
      return Promise.reject(toInterceptorError(error));
    }

    if (isSilent401Url(url)) {
      return Promise.reject(toInterceptorError(error));
    }

    const code = getAuthErrorCode(error);

    if (code === 'AUTH-0006' && originalRequest && !originalRequest._retry && wasSessionRenewedRecently()) {
      originalRequest._retry = true;
      return await apiClient(originalRequest);
    }

    if (code === 'AUTH-0006') {
      console.warn('[apiClient] Session replaced by another login (AUTH-0006)');
      forcedLogout('replaced');
      return Promise.reject(toInterceptorError(error));
    }

    if (code === 'AUTH-0003' && originalRequest && !originalRequest._retry && isRenewableUrl(url)) {
      originalRequest._retry = true;
      try {
        if (wasSessionRenewedRecently()) {
          return await apiClient(originalRequest);
        }
        const renewed = await renewToken();
        if (renewed.success || renewed.code === 'AUTH-0005') {
          return await apiClient(originalRequest);
        }
        forcedLogout('expired');
        return Promise.reject(toInterceptorError(error));
      } catch {
        forcedLogout('expired');
        return Promise.reject(toInterceptorError(error));
      }
    }

    console.warn('[apiClient] Unauthorized request detected');
    forcedLogout(code === 'AUTH-0003' ? 'expired' : 'unknown');
    return Promise.reject(toInterceptorError(error));
  }
);

export default apiClient;

export { apiClient };
